import { useState } from 'react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  DollarSign, TrendingUp, TrendingDown, FileText,
  PieChart, Building2, CheckCircle, Clock, AlertCircle, Wallet,
  Briefcase, ExternalLink, Users, Receipt
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function AccountingDashboard({ dashboard, fiscalYear, navigate, setActiveTab }) {
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showIncomeDetailsModal, setShowIncomeDetailsModal] = useState(false);
  const [showExpenseDetailsModal, setShowExpenseDetailsModal] = useState(false);
  const [incomeDetails, setIncomeDetails] = useState(null);
  const [expenseDetails, setExpenseDetails] = useState(null);

  const fetchIncomeDetails = async () => {
    setDetailsLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/api/accounting/dashboard/income-details?fiscal_year=${fiscalYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIncomeDetails(response.data);
      setShowIncomeDetailsModal(true);
    } catch (err) {
      toast.error('Failed to load income details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchExpenseDetails = async () => {
    setDetailsLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(
        `${API}/api/accounting/dashboard/expense-details?fiscal_year=${fiscalYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExpenseDetails(response.data);
      setShowExpenseDetailsModal(true);
    } catch (err) {
      toast.error('Failed to load expense details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchNetProfitDetails = async () => {
    setDetailsLoading(true);
    try {
      const token = getToken();
      const [incomeRes, expenseRes] = await Promise.all([
        axios.get(`${API}/api/accounting/dashboard/income-details?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/accounting/dashboard/expense-details?fiscal_year=${fiscalYear}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setIncomeDetails(incomeRes.data);
      setExpenseDetails(expenseRes.data);
      setShowIncomeDetailsModal(true);
    } catch (err) {
      toast.error('Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const navigateToCommission = (commission) => {
    setShowIncomeDetailsModal(false);
    setShowExpenseDetailsModal(false);
    if (commission.division === 'insurance') {
      localStorage.setItem('businessUnit', 'insurance');
    } else {
      localStorage.setItem('businessUnit', 'mortgage');
    }
    const searchTerm = commission.description || commission.agent_name || '';
    navigate(`/commissions?search=${encodeURIComponent(searchTerm)}`);
  };

  const navigateToExpense = (expense) => {
    setShowIncomeDetailsModal(false);
    setShowExpenseDetailsModal(false);
    if (expense.division === 'insurance') {
      localStorage.setItem('businessUnit', 'insurance');
    } else {
      localStorage.setItem('businessUnit', 'mortgage');
    }
    navigate('/expenses');
  };

  const navigateToTransaction = () => {
    setShowIncomeDetailsModal(false);
    setShowExpenseDetailsModal(false);
    setActiveTab('transactions');
  };

  if (!dashboard) return null;

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card 
          className="cursor-pointer hover:shadow-lg hover:border-emerald-300 transition-all"
          onClick={fetchIncomeDetails}
          data-testid="income-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Total Income</p>
                <p className="text-xl font-bold font-mono text-emerald-600">{formatCurrency(dashboard.summary.total_income)}</p>
                <p className="text-xs text-emerald-600 mt-1">Click for details →</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg hover:border-red-300 transition-all"
          onClick={fetchExpenseDetails}
          data-testid="expense-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Total Expenses</p>
                <p className="text-xl font-bold font-mono text-red-600">{formatCurrency(dashboard.summary.total_expenses)}</p>
                <p className="text-xs text-red-600 mt-1">Click for details →</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all"
          onClick={fetchNetProfitDetails}
          data-testid="net-profit-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Net Brokerage Earnings</p>
                <p className={`text-xl font-bold font-mono ${dashboard.summary.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatCurrency(dashboard.summary.net_brokerage_earnings || dashboard.summary.net_profit)}
                </p>
                <p className="text-xs text-blue-600 mt-1">Click for breakdown →</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase">Invoices</p>
                <p className="text-xl font-bold font-mono">{dashboard.invoices.count}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Division Breakdown */}
      {dashboard.division_breakdown && (
        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <PieChart className="w-5 h-5 text-slate-600" />
              Division Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-semibold text-blue-900">Mortgage Division</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-700">Commission Income</span>
                    <span className="font-mono font-semibold text-blue-900">{formatCurrency(dashboard.division_breakdown.mortgage?.income || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-blue-700">Agent Payouts</span>
                    <span className="font-mono font-semibold text-red-600">-{formatCurrency(dashboard.division_breakdown.mortgage?.commission_payouts || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-blue-200 pt-2">
                    <span className="text-sm font-medium text-blue-700">Transactions</span>
                    <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                      {dashboard.division_breakdown.mortgage?.transaction_count || 0}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
                    <Briefcase className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="font-semibold text-emerald-900">Insurance & Investment</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-emerald-700">Commission Income</span>
                    <span className="font-mono font-semibold text-emerald-900">{formatCurrency(dashboard.division_breakdown.insurance?.income || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-emerald-700">Agent Payouts</span>
                    <span className="font-mono font-semibold text-red-600">-{formatCurrency(dashboard.division_breakdown.insurance?.commission_payouts || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t border-emerald-200 pt-2">
                    <span className="text-sm font-medium text-emerald-700">Transactions</span>
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300">
                      {dashboard.division_breakdown.insurance?.transaction_count || 0}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Monthly Chart */}
      {dashboard.monthly_breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Monthly Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dashboard.monthly_breakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => formatCurrency(v)} />
                <Bar dataKey="income" name="Income" fill="#10b981" />
                <Bar dataKey="expenses" name="Expenses" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Invoice Summary */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-emerald-700">{formatCurrency(dashboard.invoices.paid)}</p>
            <p className="text-sm text-emerald-600">Paid Invoices</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 text-amber-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-amber-700">{formatCurrency(dashboard.invoices.pending)}</p>
            <p className="text-sm text-amber-600">Pending</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-700">{formatCurrency(dashboard.invoices.overdue)}</p>
            <p className="text-sm text-red-600">Overdue</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <Wallet className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700">{formatCurrency(dashboard.invoices.total)}</p>
            <p className="text-sm text-blue-600">Total Invoiced</p>
          </CardContent>
        </Card>
      </div>

      {/* Income Details Modal */}
      <Dialog open={showIncomeDetailsModal} onOpenChange={setShowIncomeDetailsModal}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Income Details - {fiscalYear}
            </DialogTitle>
            <DialogDescription>
              Breakdown of all income sources. Click any row to view the source transaction.
            </DialogDescription>
          </DialogHeader>

          {incomeDetails && (
            <div className="flex-1 overflow-auto space-y-6 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-50 rounded-lg p-4">
                  <p className="text-xs text-emerald-600 uppercase">Gross Commission</p>
                  <p className="text-xl font-bold font-mono text-emerald-700">{formatCurrency(incomeDetails.totals.gross_commission)}</p>
                  <p className="text-xs text-emerald-600 mt-1">{incomeDetails.commissions.length} transactions</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-blue-600 uppercase">Chart of Accounts</p>
                  <p className="text-xl font-bold font-mono text-blue-700">{formatCurrency(incomeDetails.totals.chart_of_accounts)}</p>
                  <p className="text-xs text-blue-600 mt-1">{incomeDetails.chart_of_accounts.length} transactions</p>
                </div>
                <div className="bg-slate-100 rounded-lg p-4">
                  <p className="text-xs text-slate-600 uppercase">Total Income</p>
                  <p className="text-xl font-bold font-mono text-slate-900">{formatCurrency(incomeDetails.totals.total)}</p>
                </div>
              </div>

              {incomeDetails.commissions.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" /> Gross Commissions ({incomeDetails.commissions.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '400px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Statement #</th>
                          <th className="text-left p-2 font-medium">Agent</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Lender</th>
                          <th className="text-left p-2 font-medium">Division</th>
                          <th className="text-right p-2 font-medium">Gross Commission</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {incomeDetails.commissions.map((comm, idx) => (
                          <tr key={idx} className="hover:bg-emerald-50 cursor-pointer transition-colors" onClick={() => navigateToCommission(comm)}>
                            <td className="p-2">{comm.date}</td>
                            <td className="p-2 font-mono text-xs">{comm.statement_no}</td>
                            <td className="p-2">{comm.agent_name}</td>
                            <td className="p-2 max-w-xs truncate" title={comm.description}>{comm.description}</td>
                            <td className="p-2">{comm.lender}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={comm.division === 'insurance' ? 'text-purple-600 border-purple-300' : 'text-blue-600 border-blue-300'}>
                                {comm.division}
                              </Badge>
                            </td>
                            <td className="p-2 text-right font-mono text-emerald-600 font-medium">{formatCurrency(comm.gross_commission)}</td>
                            <td className="p-2 text-center"><ExternalLink className="w-4 h-4 text-slate-400" /></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-emerald-100 sticky bottom-0">
                        <tr>
                          <td colSpan={6} className="p-2 font-semibold text-right">Total Gross Commission:</td>
                          <td className="p-2 text-right font-mono font-bold text-emerald-700">{formatCurrency(incomeDetails.totals.gross_commission)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {incomeDetails.chart_of_accounts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Chart of Accounts Income ({incomeDetails.chart_of_accounts.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '300px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Account</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Reference</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {incomeDetails.chart_of_accounts.map((trans, idx) => (
                          <tr key={idx} className="hover:bg-blue-50 cursor-pointer transition-colors" onClick={() => navigateToTransaction(trans)}>
                            <td className="p-2">{trans.date}</td>
                            <td className="p-2">{trans.account_name}</td>
                            <td className="p-2 max-w-xs truncate" title={trans.description}>{trans.description}</td>
                            <td className="p-2">{trans.reference}</td>
                            <td className="p-2 text-right font-mono text-blue-600 font-medium">{formatCurrency(trans.amount)}</td>
                            <td className="p-2 text-center"><ExternalLink className="w-4 h-4 text-slate-400" /></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-blue-100 sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="p-2 font-semibold text-right">Total Chart of Accounts:</td>
                          <td className="p-2 text-right font-mono font-bold text-blue-700">{formatCurrency(incomeDetails.totals.chart_of_accounts)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIncomeDetailsModal(false)}>Close</Button>
            {expenseDetails && (
              <Button onClick={() => { setShowIncomeDetailsModal(false); setShowExpenseDetailsModal(true); }}>
                View Expenses →
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Details Modal */}
      <Dialog open={showExpenseDetailsModal} onOpenChange={setShowExpenseDetailsModal}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              Expense Details - {fiscalYear}
            </DialogTitle>
            <DialogDescription>
              Breakdown of all expense sources. Click any row to view the source transaction.
            </DialogDescription>
          </DialogHeader>

          {expenseDetails && (
            <div className="flex-1 overflow-auto space-y-6 py-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-orange-50 rounded-lg p-3">
                  <p className="text-xs text-orange-600 uppercase">Agent Commission</p>
                  <p className="text-lg font-bold font-mono text-orange-700">{formatCurrency(expenseDetails.totals.agent_commissions)}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-xs text-amber-600 uppercase">Co-Agent Comm.</p>
                  <p className="text-lg font-bold font-mono text-amber-700">{formatCurrency(expenseDetails.totals.co_agent_commissions)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-xs text-purple-600 uppercase">Royalty + GST</p>
                  <p className="text-lg font-bold font-mono text-purple-700">{formatCurrency(expenseDetails.totals.royalty_gst)}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-xs text-red-600 uppercase">Expense Records</p>
                  <p className="text-lg font-bold font-mono text-red-700">{formatCurrency(expenseDetails.totals.expense_records)}</p>
                  <p className="text-xs text-red-500 mt-1">{expenseDetails.expense_records.length} records</p>
                </div>
                <div className="bg-slate-100 rounded-lg p-3">
                  <p className="text-xs text-slate-600 uppercase">Total Expenses</p>
                  <p className="text-lg font-bold font-mono text-slate-900">{formatCurrency(expenseDetails.totals.total)}</p>
                </div>
              </div>

              {expenseDetails.commission_payouts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" /> Commission Payouts ({expenseDetails.commission_payouts.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '350px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Agent</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Division</th>
                          <th className="text-right p-2 font-medium">Agent Comm.</th>
                          <th className="text-right p-2 font-medium">Co-Agent</th>
                          <th className="text-right p-2 font-medium">Royalty+GST</th>
                          <th className="text-right p-2 font-medium">Total</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenseDetails.commission_payouts.map((payout, idx) => (
                          <tr key={idx} className="hover:bg-orange-50 cursor-pointer transition-colors" onClick={() => navigateToCommission(payout)}>
                            <td className="p-2">{payout.date}</td>
                            <td className="p-2">{payout.agent_name}</td>
                            <td className="p-2 max-w-xs truncate" title={payout.description}>{payout.description}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={payout.division === 'insurance' ? 'text-purple-600 border-purple-300' : 'text-blue-600 border-blue-300'}>
                                {payout.division}
                              </Badge>
                            </td>
                            <td className="p-2 text-right font-mono text-orange-600">{formatCurrency(payout.agent_commission)}</td>
                            <td className="p-2 text-right font-mono text-amber-600">{formatCurrency(payout.co_agent_commission)}</td>
                            <td className="p-2 text-right font-mono text-purple-600">{formatCurrency(payout.royalty_gst)}</td>
                            <td className="p-2 text-right font-mono font-medium">{formatCurrency(payout.total)}</td>
                            <td className="p-2 text-center"><ExternalLink className="w-4 h-4 text-slate-400" /></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-orange-100 sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="p-2 font-semibold text-right">Totals:</td>
                          <td className="p-2 text-right font-mono font-bold text-orange-700">{formatCurrency(expenseDetails.totals.agent_commissions)}</td>
                          <td className="p-2 text-right font-mono font-bold text-amber-700">{formatCurrency(expenseDetails.totals.co_agent_commissions)}</td>
                          <td className="p-2 text-right font-mono font-bold text-purple-700">{formatCurrency(expenseDetails.totals.royalty_gst)}</td>
                          <td className="p-2 text-right font-mono font-bold">{formatCurrency(expenseDetails.totals.agent_commissions + expenseDetails.totals.co_agent_commissions + expenseDetails.totals.royalty_gst)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {expenseDetails.expense_records.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <Receipt className="w-4 h-4" /> Expense Records ({expenseDetails.expense_records.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '300px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Agent</th>
                          <th className="text-left p-2 font-medium">Category</th>
                          <th className="text-left p-2 font-medium">Vendor</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Division</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenseDetails.expense_records.map((exp, idx) => (
                          <tr key={idx} className="hover:bg-red-50 cursor-pointer transition-colors" onClick={() => navigateToExpense(exp)}>
                            <td className="p-2">{exp.date}</td>
                            <td className="p-2">{exp.agent_name}</td>
                            <td className="p-2">{exp.category}</td>
                            <td className="p-2">{exp.vendor}</td>
                            <td className="p-2 max-w-xs truncate" title={exp.description}>{exp.description}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={exp.division === 'insurance' ? 'text-purple-600 border-purple-300' : 'text-blue-600 border-blue-300'}>
                                {exp.division}
                              </Badge>
                            </td>
                            <td className="p-2 text-right font-mono text-red-600 font-medium">{formatCurrency(exp.amount)}</td>
                            <td className="p-2 text-center"><ExternalLink className="w-4 h-4 text-slate-400" /></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-red-100 sticky bottom-0">
                        <tr>
                          <td colSpan={6} className="p-2 font-semibold text-right">Total Expense Records:</td>
                          <td className="p-2 text-right font-mono font-bold text-red-700">{formatCurrency(expenseDetails.totals.expense_records)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {expenseDetails.chart_of_accounts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Chart of Accounts Expenses ({expenseDetails.chart_of_accounts.length} records)
                  </h4>
                  <div className="border rounded-lg overflow-auto" style={{ maxHeight: '250px' }}>
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2 font-medium">Date</th>
                          <th className="text-left p-2 font-medium">Account</th>
                          <th className="text-left p-2 font-medium">Description</th>
                          <th className="text-left p-2 font-medium">Reference</th>
                          <th className="text-right p-2 font-medium">Amount</th>
                          <th className="text-center p-2 font-medium w-10"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {expenseDetails.chart_of_accounts.map((trans, idx) => (
                          <tr key={idx} className="hover:bg-slate-100 cursor-pointer transition-colors" onClick={() => navigateToTransaction(trans)}>
                            <td className="p-2">{trans.date}</td>
                            <td className="p-2">{trans.account_name}</td>
                            <td className="p-2 max-w-xs truncate" title={trans.description}>{trans.description}</td>
                            <td className="p-2">{trans.reference}</td>
                            <td className="p-2 text-right font-mono text-red-600 font-medium">{formatCurrency(trans.amount)}</td>
                            <td className="p-2 text-center"><ExternalLink className="w-4 h-4 text-slate-400" /></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-slate-200 sticky bottom-0">
                        <tr>
                          <td colSpan={4} className="p-2 font-semibold text-right">Total Chart of Accounts:</td>
                          <td className="p-2 text-right font-mono font-bold">{formatCurrency(expenseDetails.totals.chart_of_accounts)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {incomeDetails && (
              <Button variant="outline" onClick={() => { setShowExpenseDetailsModal(false); setShowIncomeDetailsModal(true); }}>
                ← View Income
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowExpenseDetailsModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
